/**
 * Welk voertuig je rijdt, en wat dat betekent.
 *
 * ------------------------------------------------------------------
 * Zuiver en apart, om dezelfde reden als `boardFilter.js` en `ritRegel.js`:
 * dit is een regel, geen tekening, en hij moet te toetsen zijn zonder scherm.
 *
 * **De tabel staat hier niet.** Die komt uit `GET /api/config/all`
 * (`profielen`), want hij staat ook in Android en iOS en drie kopieën lopen
 * uit elkaar zonder dat iets het merkt. Wat hier staat is hoe je hem leest,
 * plus een terugval voor de koude start — zelfde afweging als
 * `fallbackTypes.js`, en met dezelfde plicht om hem met de hand gelijk te
 * houden aan `backend/config.py`.
 *
 * **De vangrail: bij twijfel zie je méér.** Een onbekend profiel, een
 * catalogus die nog niet geland is, een meldingssoort die wij niet kennen —
 * in al die gevallen is het antwoord "wel tonen". Een gemiste waarschuwing is
 * duurder dan een overbodige, en een leeg bord door een gefaalde configuratie
 * is precies het soort stille fout waar dit project al een paar keer op
 * gevallen is.
 */

/** Wie niets kiest, is automobilist: dat verbergt niets. */
export const STANDAARD = "auto";

/** Waar de keuze op dit toestel ligt. */
const SLEUTEL = "meldradar_voertuig";
const SLEUTEL_EXTRA = "meldradar_voertuig_extra";

/**
 * De tabel zoals de server hem geeft, voor als hij er nog niet is.
 * Handmatig gelijk te houden met `PROFIELEN` in backend/config.py.
 */
export const TERUGVAL = [
  { id: "auto", naam: "Automobilist", onder: "Alles wat iedereen aangaat",
    doelgroepen: ["iedereen"], max_kmh: null, vraag: null },
  { id: "motor", naam: "Motor", onder: "Zelfde meldingen als een auto",
    doelgroepen: ["iedereen"], max_kmh: null, vraag: null },
  { id: "vracht", naam: "Vrachtwagen", onder: "Ook weeg- en transportcontroles",
    doelgroepen: ["iedereen", "vracht"], max_kmh: 80, vraag: null },
  { id: "touringcar", naam: "Touringcar", onder: "Ook bus-, weeg- en transportcontroles",
    doelgroepen: ["iedereen", "touringcar", "vracht"], max_kmh: 80,
    vraag: { sleutel: "t100", vraag: "T100-gecertificeerd?",
             uitleg: "Met een T100-ontheffing mag je 100 op de snelweg, anders 80.",
             cap_ja: 100 } },
  { id: "taxi", naam: "Taxi", onder: "Ook taxicontroles",
    doelgroepen: ["iedereen", "taxi"], max_kmh: null, vraag: null },
];

/** Het profiel uit de tabel, of null. */
export const profielVan = (profielen, id) =>
  (Array.isArray(profielen) ? profielen : TERUGVAL).find(p => p && p.id === id) || null;

/**
 * Welke doelgroepen dit profiel aangaan.
 *
 * Een onbekend profiel geeft null, en dat betekent voor elke aanroeper
 * hieronder: alles tonen. Niet een lege lijst — dat zou een leeg scherm zijn.
 */
export const doelgroepenVoor = (profielen, id) => {
  const p = profielVan(profielen, id);
  return p && Array.isArray(p.doelgroepen) && p.doelgroepen.length
    ? p.doelgroepen
    : null;
};

/**
 * Of deze melding deze bestuurder aangaat.
 *
 * `soorten` is de typecatalogus (`useConfig().types`), waar de `audience` per
 * soort in staat. Kennen we de soort niet, dan tonen we hem — dat gebeurt bij
 * een koude start en bij een soort die de server nieuwer heeft dan wij.
 */
export const gaatMijAan = (melding, soorten, profielen, id) => {
  const toegestaan = doelgroepenVoor(profielen, id);
  if (!toegestaan) return true;
  const soort = (Array.isArray(soorten) ? soorten : []).find(
    t => t && t.id === (melding && melding.authority));
  if (!soort || !soort.audience) return true;
  return toegestaan.includes(soort.audience);
};

/** Dezelfde vraag, maar voor een soort uit de catalogus (het meldscherm). */
export const soortGaatMijAan = (soort, profielen, id) => {
  const toegestaan = doelgroepenVoor(profielen, id);
  if (!toegestaan) return true;
  if (!soort || !soort.audience) return true;
  return toegestaan.includes(soort.audience);
};

/**
 * Wat er voor deze bestuurder geldt: de laagste van bord en voertuig.
 *
 * Spiegel van `effectieve_limiet` in backend/config.py, en dat moet zo
 * blijven. Geen geposte limiet betekent geen antwoord — een voertuigcap in de
 * plaats daarvan zou een getal op het scherm zetten dat nergens staat.
 */
export const effectieveLimiet = (gepost, profielen, id, extra) => {
  const n = Number.parseInt(gepost, 10);
  if (!Number.isFinite(n)) return null;
  const p = profielVan(profielen, id);
  if (!p) return n;
  let cap = p.max_kmh;
  if (p.vraag && extra && extra[p.vraag.sleutel]) cap = p.vraag.cap_ja;
  return cap == null ? n : Math.min(n, cap);
};

/**
 * Of er iets te zeggen valt over een afwijkende limiet.
 *
 * Alleen als hij werkelijk lager is dan het bord. Anders zou er "max 80 · voor
 * jou 80" staan, en dat is ruis die de echte gevallen minder zichtbaar maakt.
 */
export const limietWijktAf = (gepost, profielen, id, extra) => {
  const eigen = effectieveLimiet(gepost, profielen, id, extra);
  const n = Number.parseInt(gepost, 10);
  return eigen != null && Number.isFinite(n) && eigen < n;
};

/**
 * De keuze lezen en schrijven.
 *
 * In een Telegram-WebView kan opslag gewist of geblokkeerd zijn, en dan komt
 * er niets terug. Dat is alleen acceptabel omdat afwezig "automobilist"
 * betekent en dus "niets verbergen" — zou de standaard ooit verbergen, dan is
 * opslagverlies een stil verlies van waarschuwingen.
 */
export const lees = () => {
  try {
    return window.localStorage.getItem(SLEUTEL) || STANDAARD;
  } catch (e) {
    return STANDAARD;
  }
};

export const schrijf = (id) => {
  try {
    window.localStorage.setItem(SLEUTEL, String(id));
  } catch (e) { /* geen opslag; de keuze geldt deze sessie */ }
};

export const leesExtra = () => {
  try {
    return JSON.parse(window.localStorage.getItem(SLEUTEL_EXTRA) || "{}") || {};
  } catch (e) {
    return {};
  }
};

export const schrijfExtra = (extra) => {
  try {
    window.localStorage.setItem(SLEUTEL_EXTRA, JSON.stringify(extra || {}));
  } catch (e) { /* idem */ }
};
